import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { ProjectWrapperType } from "@/dataProviders/ProjectWrapper/types/ProjectWrapperType";
import { ProjectAdditionalInfoWrapperType } from "@/dataProviders/ProjectWrapper/types/ProjectAdditionalInfoWrapperType";
import { getBuildingProject } from "@/api/services/BuildingProject";
import { getProjectPermissions } from "@/providers/Permissions/getProjectPermissions";
import { getProjectMaterials } from "@/api/services/Mapping";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";
import getLifetimeTableFromApi from "@/api/services/LifetimeTableApi";
import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";
import { useFilters } from "@/stores/project/project.filters";
import { isEpdExpired } from "@/services/Utils/epdValidity";
import { useEnum } from "@/stores/project/project.enum";
import { mergeMaterialUpdates } from "@/stores/project/mergeMaterialUpdates";
import type { AutoMapPendingPreview } from "@/stores/project/autoMapPreview";
import { resolveAllModelNames } from "@/stores/project/allModelNames";
import { mergeSavedProject } from "@/stores/project/mergeSavedProject";
import { resolveIncludedLifestages } from "@/stores/project/includedLifestages";
import {
  INVENTORY_HIDING_FILTER_GROUPS,
  filterGroupHasSelection,
  hasHidingInventoryFilters,
  modelSelectionNarrows,
  modelsToShow,
} from "@/stores/project/inventoryFilterState";
// The inventory's model dimension (RTLCA-5569). Imported from the store module, not the
// `@/features/Dashboard` barrel: the barrel pulls the whole feature in and is what
// ProjectDetailLayout warns about for init order. `dashboard.store.ts` does not import this store,
// so there is no cycle — and it is resolved at read time rather than held in a setup-level const,
// so nothing here depends on which of the two stores was created first.
import { useDashboard } from "@/stores/dashboard/dashboard.store";

type Project = ProjectWrapperType["project"];
type Model = ProjectWrapperType["models"][number];
type Permissions = ProjectAdditionalInfoWrapperType["userPermissions"];
export type ProjectMaterialByModel = Record<string, SpeckleMaterial[]>;

export const useProject = defineStore("project", () => {
  const filterStore = useFilters();
  const { activeMaterialFilters } = storeToRefs(filterStore);

  const enumStore = useEnum();
  const { lifeCycleStages } = storeToRefs(enumStore);

  // State
  const project = ref<Project | null>(null);
  const models = ref<Model[]>([]);
  const selectedModel = ref<string | null>(null);
  const permissions = ref<Permissions | null>(null);
  const lifetimeTable = ref<any | null>(null);
  // An auto-mapping dry run, held apart from the saved inventory until accepted or discarded
  // (RTLCA-5821) — never folded into `projectMaterial`, so a refetch of the saved rows (Dashboard
  // and back, for instance) can never clobber it and losing it never loses the request Accept needs.
  const autoMapPendingPreview = ref<AutoMapPendingPreview | null>(null);
  const selectedMaterial = ref<"type" | "material">("material");
  const projectMaterial = ref<{ materials: ProjectMaterialByModel } | null>(null);
  const projectMaterialIsLoading = ref<boolean>(false);
  // Which project projectMaterial belongs to — a project switch must never show (or preselect
  // from) the previous project's inventory while the new fetch is in flight.
  const projectMaterialProjectId = ref<string | null>(null);
  const searchMaterialMapping = ref<string | null>(null);
  const showExpiredEpdOnly = ref<boolean>(false);
  // A group the inventory should scroll to and open once it renders, e.g. one just created from a library drop.
  const inventoryFocusIdentifier = ref<string | null>(null);

  const mappingPercentage = ref<number>(0);

  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Setters
  const setInventoryFocus = (identifier: string | null) => {
    inventoryFocusIdentifier.value = identifier;
  };

  const setSelectedModel = (name: string | null) => {
    selectedModel.value = name;
  };

  const setMaterialMappingSearch = (text: string | null) => {
    searchMaterialMapping.value = text;
  };

  const toggleShowExpiredEpdOnly = () => {
    showExpiredEpdOnly.value = !showExpiredEpdOnly.value;
  };

  const setMaterialType = (name: "type" | "material") => {
    selectedMaterial.value = name;
  };

  const setProject = (projectData: Project | null) => {
    project.value = projectData;
  };

  const setPermissions = (permissionsData: Permissions | null) => {
    permissions.value = permissionsData;
  };

  const modelsFromBranches = (branches: Project["speckleData"]["branches"]): Model[] =>
    branches.map((branch) => ({
      commitId: branch.commitId ?? undefined,
      commitPulledDateTime: branch.commitPulledDateTime,
      id: branch.id,
      name: branch.name,
      referencedObjectId: branch.referencedObjectId,
    }));

  // The settings page just saved (or the dashboard's status dialog just wrote) and re-read the project into
  // its own store; take the changes here without another REST read (RTLCA-5645).
  const applySavedProject = (saved: Project) => {
    const merged = mergeSavedProject(project.value, saved);
    if (!merged || merged === project.value) return;
    project.value = merged;
    models.value = modelsFromBranches(merged.speckleData?.branches ?? []);
    if (!selectedModel.value || !allModelNames.value.includes(selectedModel.value)) {
      selectedModel.value = allModelNames.value[0];
    }
  };

  // Actions
  const fetchProject = async (id: string, force = false) => {
    autoMapPendingPreview.value = null;
    if (!force && project.value?.id === id) return;
    isLoading.value = true;
    error.value = null;
    try {
      const data = await getBuildingProject(id);
      project.value = data;
      models.value = modelsFromBranches(data.speckleData.branches ?? []);
      // Keep the user's model selection when it survives the refetch. This used to reset
      // unconditionally, which was invisible while every caller relied on the early return above to
      // skip a project it already held — but a forced refetch (a revision switch, re-entering the
      // project) then threw the selection away and snapped the dropdown back to the first model.
      // A selection that no longer exists — a model dropped by the revision being switched to — is
      // still replaced.
      if (!selectedModel.value || !allModelNames.value.includes(selectedModel.value)) {
        selectedModel.value = allModelNames.value[0];
      }
    } catch (e: any) {
      error.value = e?.message ?? "Failed to load project";
    } finally {
      isLoading.value = false;
    }
  };

  const fetchLifetimeTable = async () => {
    lifetimeTable.value = await getLifetimeTableFromApi();
  };

  const fetchProjectPermissions = async (id: string, force = false) => {
    if (!force && permissions.value !== null && project.value?.id === id) return;
    permissions.value = await getProjectPermissions(id);
  };

  const fetchProjectMaterials = async (id?: string, byType?: boolean) => {
    const projectId = id ?? project.value?.id;
    if (!projectId) return;
    const finalByType = byType ?? selectedMaterial.value === "type";
    // While reading an older revision — previewed, or made current by an admin — the materials
    // list is pinned to it (as-of read). Resolved at call time so every existing refetch call site
    // stays revision-correct.
    const revisionId = useProjectRevisionsStore().readPinRevisionIdFor(projectId) ?? undefined;
    if (projectMaterialProjectId.value !== projectId) {
      projectMaterial.value = null;
      projectMaterialProjectId.value = projectId;
    }
    projectMaterialIsLoading.value = true;
    try {
      projectMaterial.value = await getProjectMaterials(projectId, finalByType, revisionId);
    } catch {
      // intentional
    }
    projectMaterialIsLoading.value = false;
  };

  // Write a saved mapping (or any other material edit) back into projectMaterial, so the store
  // matches what the user just did without a full refetch. The mapping UI edits a deep clone of
  // what activeMaterial handed it, and activeMaterial is recomputed by every filter, search and
  // sort change — without this the clone is thrown away on the next recompute and the change
  // looks lost until a reload (RTLCA-5231).
  const applyMaterialUpdates = (updated: SpeckleMaterial | Array<SpeckleMaterial | null | undefined>) => {
    const updates = Array.isArray(updated) ? updated : [updated];
    mergeMaterialUpdates(projectMaterial.value?.materials, updates);
    // A manual edit made while a preview is pending must show up in the displayed proposals too —
    // `activeMaterial` reads from the preview's materials while one is pending (`inventoryMaterials`).
    if (autoMapPendingPreview.value) mergeMaterialUpdates(autoMapPendingPreview.value.materials, updates);
  };

  // Getters
  const allMaterials = computed(() => {
    if (!projectMaterial.value?.materials) return [];
    return Object.values(projectMaterial.value.materials).flat();
  });

  // The rows the inventory (`activeMaterial`) actually shows: a pending auto-mapping preview's
  // proposals when one exists for THIS project and THIS grouping, the saved rows otherwise
  // (RTLCA-5821). Only `activeMaterial` reads from this — `allMaterials` and everything outside
  // Material Mapping stay on the saved collection, so proposals never leak into the 3D inspector,
  // the filters, or the dashboard.
  const inventoryMaterials = computed<ProjectMaterialByModel | undefined>(() => {
    const preview = autoMapPendingPreview.value;
    if (preview && preview.projectId === project.value?.id && preview.byType === isGroupingByType.value) {
      return preview.materials;
    }
    return projectMaterial.value?.materials;
  });

  // A building component "has an expired EPD" when any of its child mappings points to an EPD whose
  // validity-end date is in the past. Mappings live on the children, not the parent group. We flag
  // every assigned expired EPD (regardless of includeMapping) — an expired assignment is worth
  // surfacing whether or not it currently counts toward the calculation.
  const materialHasExpiredEpd = (material: any): boolean => {
    const children = material.children ?? [];
    return children.some((child: any) => isEpdExpired(child.mapping?.epdInfo?.validTo));
  };

  // The project's SPECKLE branches only. Keep using this where a Speckle stream is what the surface
  // actually needs — the IFC upload's target list. Anything meaning "every model of this project"
  // wants `allModelNames` below.
  const activeModelNames = computed(() => models.value.map((m) => m.name).sort());

  // Every model of the project, whatever pushed it — Speckle branches and models pushed from
  // APS/Forma alike. This is the model dimension every "which models does this project have?"
  // surface reads; see `resolveAllModelNames` for where the list comes from and what it replaced.
  const allModelNames = computed(() => resolveAllModelNames(project.value?.modelNames, activeModelNames.value));

  const activeMaterial = computed(() => {
    if (!inventoryMaterials.value) {
      return [];
    }

    // Which models to show: the toolbar's global Models selection, which is the inventory's only
    // model dimension since the FILTERS popover's duplicate group was dropped (RTLCA-5569). Plain
    // model names on both sides — the dashboard's list is BuildingProject.GetAllModelNames(), a
    // superset of this map's keys, compared exactly. Empty selection means all.
    const selectedModelNames: string[] = useDashboard().selectedModels ?? [];
    // The models this inventory actually holds rows for — the keys of the material map, NOT the
    // store's `allModelNames`: a model with no building elements has nothing to merge in here.
    const modelNamesWithMaterials = Object.keys(inventoryMaterials.value);

    // Merge materials from selected models and annotate with modelName
    let materials = modelsToShow(modelNamesWithMaterials, selectedModelNames)
      .flatMap((modelName) => (inventoryMaterials.value?.[modelName] ?? []).map((m) => ({ ...m, modelName })))
      .sort((a: { identifier: string }, b: { identifier: string }) => a.identifier.localeCompare(b.identifier));

    if (searchMaterialMapping.value) {
      const searchLower = searchMaterialMapping.value.trim().toLowerCase();
      materials = materials.filter((m: any) => {
        // Check if parent identifier contains search term
        const parentMatches =
          (m.identifier?.toLowerCase()?.includes(searchLower) ?? false) ||
          (m.category?.toLowerCase()?.includes(searchLower) ?? false);

        // Check if any child identifier contains search term
        const children = m.children || [];

        const childMatches = children.some((child: any) => {
          const childIdentifier = child.identifier?.toLowerCase();
          const childCategory = child.category?.toLowerCase();
          return (
            (childIdentifier ? childIdentifier.includes(searchLower) : false) ||
            (childCategory ? childCategory.includes(searchLower) : false)
          );
        });

        return parentMatches || childMatches;
      });
    }

    // Material filters
    if (Object.keys(activeMaterialFilters.value).length > 0) {
      if (activeMaterialFilters.value.mappings) {
        const mappingFilters = activeMaterialFilters.value.mappings;
        const include = !!mappingFilters.include;
        const exclude = !!mappingFilters.exclude;

        materials = materials.filter((material: any) => {
          const includeMapping = material.includeMapping === true;

          // both true → show all materials
          if (include && exclude) return true;

          // include (enabled mapping) → only materials where IncludeMapping is true
          if (include) return includeMapping;

          // exclude (disabled mapping) → only materials where IncludeMapping is false
          if (exclude) return !includeMapping;

          return true;
        });
      }

      if (activeMaterialFilters.value.states) {
        const stateFilters = activeMaterialFilters.value.states;
        const unmapped = !!stateFilters.unmapped;
        const mapped = !!stateFilters.mapped;
        const error = !!stateFilters.error;
        const special = !!stateFilters.special;

        materials = materials.filter((material: any) => {
          const children = material.children || [];

          // Filter to only consider children where includeMapping is truthy (true)
          const childrenWithMapping = children.filter((child: any) => !!child.includeMapping);

          // Check if any child with includeMapping has mapping === null or undefined (unmapped)
          const hasUnmappedChild = childrenWithMapping.some((child: any) => {
            const mapping = child.mapping;
            return mapping === null || mapping === undefined;
          });

          // Check if any child with includeMapping has a mapping (mapped)
          const hasMappedChild = childrenWithMapping.some((child: any) => {
            const mapping = child.mapping;
            return mapping !== null && mapping !== undefined;
          });

          // Check for validation errors (only on children with includeMapping)
          const hasErrors = childrenWithMapping.some((child: any) => child.validationErrors && child.validationErrors.length > 0);

          // Check for special conditions
          const hasSpecialConditions = !!material.hasSpecialConditionTypeMapping;

          // If unmapped is selected, return materials with at least one unmapped child (with includeMapping)
          if (unmapped && hasUnmappedChild) {
            return true;
          }

          // If mapped is selected, return materials with at least one mapped child (with includeMapping)
          // Ignore parent's own mapping status, only check children
          if (mapped && hasMappedChild) {
            return true;
          }

          // If error is selected, return materials with validation errors
          if (error && hasErrors) {
            return true;
          }

          // If special is selected, return materials with special conditions
          if (special && hasSpecialConditions) {
            return true;
          }

          // If no states are selected, don't filter by states
          if (!unmapped && !mapped && !error && !special) {
            return true;
          }

          return false;
        });
      }
      if (activeMaterialFilters.value.datasources) {
        const datasourceFilters = activeMaterialFilters.value.datasources;

        const selectedDatasources = Object.keys(datasourceFilters)
          .filter((key) => datasourceFilters[key])
          .map((key) => Number(key));

        if (selectedDatasources.length > 0) {
          materials = materials.filter((material: any) => {
            const children = material.children || [];

            // Filter to only consider children where includeMapping is truthy (true)
            const childrenWithMapping = children.filter((child: any) => !!child.includeMapping);

            // Check if any child with includeMapping has a matching datasource
            const hasMatchingChild = childrenWithMapping.some((child: any) => {
              return selectedDatasources.includes(child.source);
            });

            // Also check parent's source if it has includeMapping enabled
            const parentMatches = material.includeMapping && selectedDatasources.includes(material.source);

            return hasMatchingChild || parentMatches;
          });
        }
      }

      if (activeMaterialFilters.value.classificationCodes) {
        const classificationFilter = activeMaterialFilters.value.classificationCodes;
        const selectedCodes = Object.keys(classificationFilter).filter((key) => classificationFilter[key]);

        if (selectedCodes.length > 0) {
          const classificationParam = project.value?.speckleConfiguration?.parameters?.classificationCode ?? "";
          materials = materials.filter((material) => {
            const code = classificationParam ? material.additionalParameters?.[classificationParam] ?? "" : "";
            if (selectedCodes.includes("__empty__") && !code) return true;
            return selectedCodes.includes(code);
          });
        }
      }

      const sorting = activeMaterialFilters.value.sorting;

      if (sorting && Array.isArray(sorting)) {
        const nameSort = sorting.find((s: any) => s.label === "Name");
        const amountSort = sorting.find((s: any) => s.label === "Amount");

        // Sort by Name
        if (nameSort && nameSort.value !== false) {
          materials.sort((a: any, b: any) => {
            const comparison = a.identifier.localeCompare(b.identifier);
            return nameSort.value === "asc" ? comparison : -comparison;
          });
        }
        // Sort by Amount (if Name sort is not active, or as secondary sort)
        else if (amountSort && amountSort.value !== false) {
          materials.sort((a: any, b: any) => {
            const aAmount = a.children?.length ?? 0;
            const bAmount = b.children?.length ?? 0;
            const comparison = aAmount - bAmount;
            return amountSort.value === "asc" ? comparison : -comparison;
          });
        }
      }
    }

    // Project-level "expired EPD" view: keep only components with an expired included mapping.
    if (showExpiredEpdOnly.value) {
      materials = materials.filter(materialHasExpiredEpd);
    }

    return materials;
  });

  // Everything narrowing the inventory right now — the FILTERS popover's groups, the toolbar's
  // global Models selection, the search box and the expired-EPD toggle. `activeMaterial.length === 0`
  // on its own cannot tell an empty project from a filter that matches nothing, and the two deserve
  // different empty states (RTLCA-5551).
  // `activeMaterialFilters` is only ever read by property access here, the same way `activeMaterial`
  // reads it: it lives in the filters store, which reaches back into this one, so handing the object
  // itself to a typed parameter makes TypeScript resolve a circular type and widen both stores to
  // `any`. The groups are reduced to a boolean before crossing into the helper.
  const hasActiveInventoryFilters = computed<boolean>(() => {
    const hasFilterGroupSelection = INVENTORY_HIDING_FILTER_GROUPS.some((groupKey) =>
      filterGroupHasSelection(activeMaterialFilters.value?.[groupKey])
    );
    const modelsNarrow = modelSelectionNarrows(
      Object.keys(projectMaterial.value?.materials ?? {}),
      useDashboard().selectedModels ?? []
    );
    return (
      modelsNarrow || hasHidingInventoryFilters(hasFilterGroupSelection, searchMaterialMapping.value, showExpiredEpdOnly.value)
    );
  });

  // Undo everything `hasActiveInventoryFilters` reports on, so the full inventory comes back in one
  // click. `sorting` is preserved: it never hid anything, and it is mirrored from a ref the toolbar
  // owns, which would not learn that it had been cleared. The global Models selection is restored to
  // every model — it is the inventory's model dimension now, so leaving it narrowed would make the
  // empty state's own action fail to undo what emptied the list.
  const resetInventoryFilters = () => {
    const sorting = activeMaterialFilters.value?.sorting;
    activeMaterialFilters.value = sorting ? { sorting } : {};
    searchMaterialMapping.value = null;
    showExpiredEpdOnly.value = false;

    // Reassigning the selection is enough to refetch: the dashboard store watches `selectedModels`
    // deeply and that watcher is the single driver of its reload (dashboard.store.ts `resetFilters`
    // relies on the same thing). Calling `refreshDashboard()` on top would put a second, identical
    // request on the wire for `fetchSeq` to throw away.
    const dashboard = useDashboard();
    dashboard.setSelectedModels([...dashboard.models]);
  };

  const expiredMappedCount = computed(() => activeMaterial.value.filter(materialHasExpiredEpd).length);

  // If nothing expired remains (all fixed, or filtered out by the model selection), drop the
  // "expired only" view so the user is never stranded on an empty list with no way to toggle off.
  watch(expiredMappedCount, (count) => {
    if (count === 0) showExpiredEpdOnly.value = false;
  });

  // Helper getter for library store to access project year
  const projectYearOfCommissioning = computed(() => {
    return project.value?.emissionCalculationAssumptions?.emissionMetaData?.yearOfCommissioning || "";
  });

  const isGroupingByType = computed(() => selectedMaterial.value === "type");

  // Kept as computed aliases over `autoMapPendingPreview` so existing readers
  // (MaterialDetailView.vue, CompleteAutoMappingModal.vue) keep working unchanged. Gated on the
  // preview's own project id, the same check `inventoryMaterials` above applies — a preview a user
  // ran on a different project and then navigated away from must not read as pending here either
  // (RTLCA-5821).
  const autoMappingPreviewLoaded = computed(
    () => autoMapPendingPreview.value !== null && autoMapPendingPreview.value.projectId === project.value?.id
  );
  const autoMappingConflicts = computed(() =>
    autoMappingPreviewLoaded.value ? autoMapPendingPreview.value?.conflicts.length ?? 0 : 0
  );

  /**
   * The lifecycle stages included in the calculation, as labels — and **display-only**.
   *
   * It follows the revision being VIEWED, not the working state: while previewing a frozen revision
   * it reports that revision's own stages, so the Transport (A4) / Waste (A5) nav items describe the
   * save point on screen. A write surface must NOT gate on this — it would offer, or hide, inputs
   * that belong to a state the user is not editing. (`hasPhaseB6` has no consumers yet; whoever
   * picks it up for an editable form needs the live head instead, the same caveat
   * `readOperationalFor` carries for the edit wizard.)
   *
   * The revision id is resolved inside the computed rather than captured outside it, so the pin is
   * whatever it is at read time — the same rule `fetchProjectMaterials` follows.
   */
  const includedLifestages = computed<string[]>(() => {
    const projectId = project.value?.id;
    // MobileMenu renders app-wide via TopNav, so this evaluates on routes that have no project;
    // returning early keeps it from reaching into the revisions store there.
    if (!projectId) return [];

    return resolveIncludedLifestages(
      useProjectRevisionsStore().readLifecycleStagesFor(projectId),
      project.value?.emissionCalculationAssumptions?.emissionMetaData,
      lifeCycleStages.value
    );
  });

  const hasPhaseA4 = computed(() => includedLifestages.value.includes("A4"));
  const hasPhaseA5 = computed(() => includedLifestages.value.includes("A5"));
  const hasPhaseB6 = computed(() => includedLifestages.value.includes("B6"));

  return {
    // state
    project,
    models,
    selectedModel,
    permissions,
    projectMaterial,
    projectMaterialIsLoading,
    projectMaterialProjectId,
    searchMaterialMapping,
    selectedMaterial,
    isLoading,
    error,
    lifetimeTable,
    autoMapPendingPreview,
    mappingPercentage,
    showExpiredEpdOnly,
    inventoryFocusIdentifier,

    // setters
    setInventoryFocus,
    setSelectedModel,
    setMaterialMappingSearch,
    setMaterialType,
    setProject,
    setPermissions,
    toggleShowExpiredEpdOnly,

    // actions
    resetInventoryFilters,
    fetchProject,
    applySavedProject,
    fetchProjectPermissions,
    fetchProjectMaterials,
    fetchLifetimeTable,
    applyMaterialUpdates,

    // getters
    activeModelNames,
    allModelNames,
    activeMaterial,
    inventoryMaterials,
    isGroupingByType,
    autoMappingPreviewLoaded,
    autoMappingConflicts,
    projectYearOfCommissioning,
    allMaterials,
    hasActiveInventoryFilters,
    expiredMappedCount,
    includedLifestages,
    hasPhaseA4,
    hasPhaseA5,
    hasPhaseB6,
  };
});
